/**
 * English Translations
 */
export const en = {
    // General
    app_title: 'Dueli',
    app_subtitle: 'Connect via Competition',
    home: 'Home',
    explore: 'Explore',
    live: 'Live Stream',
    // 7.A: WebRTC signaling subkeys (dedicated namespace, no new String).
    live_signaling: {
        connecting: 'Connecting...',
        connected: 'Connected',
        connection_failed: 'Connection failed',
        permission_denied: 'Permission denied',
        // 7.D: restricted networks / TURN
        network_restricted: 'Your network may be restricted — trying to connect through a relay server',
        turn_unavailable: 'TURN relay is unavailable right now — please retry or switch networks',
        // R4-LIVE-INT-1: production live-room join failures (toast, not console)
        room_create_failed: 'Failed to create signaling room. Please reload and retry.',
        session_join_failed: 'Failed to join the live session. Please reload and retry.',
        // R4-CHUNK-VIEW-RECOVERY-1 (28.4): recording ended with no playable clip
        recording_ended: 'The recording has ended — no playable clips are available.',
    },
    recorded: 'Recorded',
    upcoming: 'Upcoming',
    profile: 'Profile',
    settings: 'Settings',
    logout: 'Logout',
    login: 'Login',
    register: 'Register',
    search_placeholder: 'Search for a duel, scientist, or talent...',
    filter: 'Filter',
    all: 'All',
    help: 'Help',
    skip_to_content: 'Skip to main content',
    docs: 'Documents',
    docs_tagline: 'Project, product and engineering notes published by the Dueli team.',
    docs_empty: 'No published documents yet.',
    docs_back: 'Back to documents',
    help_guide: {
        title: 'Help & guides',
        subtitle: 'How to create, compete, and watch on Dueli — plus answers to common questions.',
        learn_more: 'Learn more',
        nav_overview: 'Overview',
        nav_roles: 'Role guides',
        nav_topics: 'Topics',
        nav_faq: 'FAQ',
        nav_access: 'Accessibility',
        overview_t: 'How Dueli works',
        overview_d: 'Create a competition, bring one opponent through invites or join requests, go live, then keep the result and any recording on the competition page.',
        roles_t: 'Guides by role',
        creator_t: 'Creator',
        creator_s1t: 'Create the competition',
        creator_s1d: 'Open Create, pick a category and subcategory, write clear rules, and optionally set a scheduled time. The competition starts as pending until an opponent joins.',
        creator_s2t: 'Invite an opponent',
        creator_s2d: 'Send an invite from the competition page or answer join requests in My Requests. A pending invite blocks a parallel join request for the same person, and one acceptance fills the single opponent seat.',
        creator_s3t: 'Go live and finish',
        creator_s3d: 'Start the stream when both sides are ready, and end it from the room controls. After the end, ratings close immediately and the result becomes final and read-only.',
        opponent_t: 'Opponent',
        opponent_s1t: 'Find a competition or accept an invite',
        opponent_s1d: 'Browse Explore for pending competitions, or open an invite from My Requests. Accept or decline from the competition page; invites expire after 24 hours.',
        opponent_s2t: 'Join the live room',
        opponent_s2d: 'Enter from the competition page when it goes live. Use the room controls for camera, microphone, and connection, and use retry if the media state shows an error.',
        opponent_s3t: 'After the competition',
        opponent_s3d: 'Read the final result on the competition page. If a recording is available it plays there; a completed competition without a recording shows the result only.',
        viewer_t: 'Viewer',
        viewer_s1t: 'Watch live',
        viewer_s1d: 'Open a live competition from Home or Explore. Live viewers can comment while the stream runs.',
        viewer_s2t: 'Rate after 5 minutes',
        viewer_s2d: 'After 300 seconds of live watch time in the same competition, you can rate each competitor 1–5 stars. You may rate both sides, change your rating until the end, and see a provisional tally marked temporary while live.',
        viewer_s3t: 'Follow and react',
        viewer_s3d: 'Give a Like or Dislike (one active reaction — switching never double-counts), follow competitors from their profile, and keep discussing in comments after the end.',
        topics_t: 'Topics',
        topic_create_t: 'Creating a competition',
        topic_create_d: 'Title, category, rules, and an optional scheduled time. Pending competitions wait for an opponent; a closed competition disables invite and join actions with a reason.',
        topic_invite_t: 'Invites and join requests',
        topic_invite_d: 'A pending invite for someone blocks a parallel join request from them, and vice versa. Accept or decline from the competition page; expired or declined rows free the path again. Races resolve to exactly one opponent.',
        topic_live_t: 'Live room states',
        topic_live_d: 'The room shows waiting, playing, error, processing, ready, or unavailable based on real media data. Waiting means no playable stream yet; on error use the retry control.',
        topic_recording_t: 'Recordings',
        topic_recording_d: 'A completed competition with a valid recording plays on its page. Completed without a recording is a result page, not a watchable video.',
        topic_ratings_t: 'Ratings (300 seconds)',
        topic_ratings_d: 'Eligibility needs 300 seconds of cumulative live watch time in the same competition — opening the page is not enough. Both competitors are rated 1–5, edits replace the previous value, every write stops at the end, and ties or no ratings leave no fake winner.',
        topic_reactions_t: 'Like and Dislike',
        topic_reactions_d: 'One active reaction per person: like, dislike, or neutral. Switching sides moves the count; it never adds twice.',
        topic_comments_t: 'Comments and timed comments',
        topic_comments_d: 'Comments stay open after the end. A comment made while a recording plays can carry its video position, follows playback as it reaches that moment, and the final list stays in chronological order.',
        topic_discover_t: 'Discovery, search, and favorites',
        topic_discover_d: 'Filter Explore by category, subcategory, status, and language; the address bar keeps the filters. Save favorite topics in Settings to personalize suggestions.',
        topic_payout_t: 'Earnings, payouts, and donations',
        topic_payout_d: 'Save payout methods in Earnings and pick one when requesting a withdrawal; the request keeps a frozen snapshot of the method so later edits never change an approved request. Donations are shown on the donate page as non-refundable, with your history under My Donations.',
        topic_support_t: 'Help, reports, and contact',
        topic_support_d: 'Use Contact Admin in the menu to open the admin-messages tab; official replies come from Dueli, not a personal account. Submit reports from the Reports page and track complaints under My Complaints. The Admin panel is visible to admins only.',
        faq_t: 'Frequently asked questions',
        faq_q1: 'How do I create a competition?',
        faq_a1: 'Go to Create, fill in title, category, and rules, then invite an opponent from the competition page. See the Creator guide above.',
        faq_q2: 'Why can I not request to join?',
        faq_a2: 'You may already have a pending invite for that competition, the opponent seat may be filled, or the competition may be closed. The page shows the reason.',
        faq_q3: 'How long before I can rate?',
        faq_a3: 'After 300 seconds of live watch time in the same competition. The rating card shows whether you are eligible yet.',
        faq_q4: 'Can I change my rating?',
        faq_a4: 'Yes — until the live ends. Each change replaces your previous value, and the live tally is marked provisional until the final result.',
        faq_q5: 'Why is the result read-only now?',
        faq_a5: 'The competition ended. Ratings close at the end and the final result, including any tie handling, is frozen.',
        faq_q6: 'Where is the recording?',
        faq_a6: 'On the competition page, when a valid recording exists. Otherwise the page shows the result without a player.',
        faq_q7: 'What do Like and Dislike do?',
        faq_a7: 'They are content reactions with one active value per person. Switching never double-counts.',
        faq_q8: 'How do I withdraw earnings?',
        faq_a8: 'Add a payout method in Earnings, then choose it when requesting a withdrawal. The request keeps a snapshot of the method details.',
        faq_q9: 'Are donations refundable?',
        faq_a9: 'The donate page marks donations as non-refundable. Your history appears under My Donations.',
        faq_q10: 'How do I contact support or report abuse?',
        faq_a10: 'Use Contact Admin for the admin-messages tab, Reports to submit a report, and My Complaints to track it.',
        access_t: 'Keyboard, zoom, and language',
        access_d: 'All Help pages work keyboard-only: Tab moves, Enter opens, Escape closes dialogs. Pinch-zoom stays enabled, Arabic reads right-to-left and English left-to-right. If anything blocks you, contact the admin from the menu.',
    },
    theme: 'Dark/Light Mode',
    language: 'Language',
    country_language: 'Country & Language',
    search_country: 'Search country...',
    users: 'Users',
    verified: 'Verified',
    busy_in_stream: 'Busy in stream',
    search_results_for: 'Search results for',
    no_users: 'No users found',
    search_users_prompt: 'Enter a search term to find users',
    // Search results — 6+6 structure with view-all (owner acceptance, no retrieval change)
    view_all_competitions: 'View all competitions',
    view_all_users: 'View all users',
    back_to_results: 'Back to results',
    filters: 'Filters',
    showing_first: 'Showing first results',

    // Categories
    categories: {
        dialogue: 'Dialogue',
        science: 'Science',
        talents: 'Talents',

        // Dialogue subcategories
        religions: 'Religious Dialogue',
        sects: 'Sectarian Dialogue',
        politics: 'Political Dialogue',
        economics: 'Economic Dialogue',
        ethnic_conflicts: 'Ethnic Conflicts',
        local_events: 'Local Events',
        global_events: 'Global Events',
        disputes: 'Other Disputes',
        other_disputes: 'Other Disputes',

        // Science subcategories
        physics: 'Physics',
        chemistry: 'Chemistry',
        math: 'Mathematics',
        astronomy: 'Astronomy',
        biology: 'Biology',
        technology: 'Technology',
        energy: 'Energy',
        economics_science: 'Economics',
        mixed: 'Mixed Sciences',
        other_science: 'Other Sciences',

        // Talents subcategories
        physical: 'Physical Talents',
        mental: 'Mental Talents',
        vocal: 'Vocal Talents',
        poetry: 'Poetic Talents',
        psychological: 'Psychological',
        creative: 'Creative Talents',
        crafts: 'Crafts',
        other_talents: 'Other Talents'
    },

    // Sections
    sections: {
        suggested: 'Suggested for You',
        dialogue: 'Dialogue Arena',
        science: 'Science Lab',
        talents: 'Talent Stage'
    },

    // Dialogue subcategories
    religions: 'Religions',
    sects: 'Sects',
    politics: 'Politics',
    economics: 'Economics',
    current_affairs: 'Current Affairs',
    disputes: 'Other Disputes',

    // Science subcategories
    physics: 'Physics',
    biology: 'Biology',
    chemistry: 'Chemistry',
    math: 'Mathematics',
    technology: 'Technology',
    medicine: 'Medicine',
    philosophy: 'Philosophy',

    // Talents subcategories
    singing: 'Singing',
    poetry: 'Poetry',
    art: 'Art',
    sports: 'Sports',
    comedy: 'Comedy',
    cooking: 'Cooking',
    gaming: 'Gaming',
    magic: 'Magic',

    // Competitions
    competition: {
        self: 'Competition',
        // ── B12: competition result (winner/draw/pending) ──────────────
        winner: 'Winner',
        draw: 'Draw',
        pending_result: 'Result not decided yet',
    },
    competitions: 'Competitions',
    create_competition: 'Create Competition',
    join_competition: 'Join Competition',
    watch_competition: 'Watch Competition',
    competition_title: 'Competition Title',
    competition_rules: 'Competition Rules',
    competition_description: 'Competition Description',
    select_category: 'Select Category',
    select_subcategory: 'Select Subcategory',
    scheduled_time: 'Scheduled Time',
    request_join: 'Request to Join',
    cancel_request: 'Cancel Request',
    request_sent: 'Request sent',
    rules_placeholder: '1. Speaking time per side\n2. Dialogue rules\n3. Evaluation criteria',
    error_occurred: 'Error occurred',

    // Statuses
    status_pending: 'Waiting for opponent',
    status_accepted: 'Accepted',
    status_declined: 'Declined',
    status_rejected: 'Rejected',
    status_auto_declined: 'Auto-declined',
    status_expired: 'Expired',
    status_live: 'Live',
    status_completed: 'Completed',
    status_cancelled: 'Cancelled',
    status_ongoing: 'Ongoing',

    // Actions
    invite: 'Invite',
    accept: 'Accept',
    decline: 'Decline',
    cancel: 'Cancel',
    send: 'Send',
    save: 'Save',
    edit: 'Edit',
    delete: 'Delete',
    follow: 'Follow',
    unfollow: 'Unfollow',
    block: 'Block',
    unblock: 'Unblock',
    confirm_block: 'Block this user? They will not be able to message or interact with you.',
    confirm_unblock: 'Unblock this user?',
    user_blocked: 'User blocked.',
    user_unblocked: 'User unblocked.',

    // Rating
    rate: 'Rate',
    rating: 'Rating',
    viewers: 'views',
    // R2-L1: live presence is displayed separately from the counted total.
    viewers_now: 'watching now',

    // Comments
    comment: 'Comment',
    comments: {
        label: 'Comments',
        load_more: 'Load more comments',
        no_comments: 'No comments yet',
        reply: 'Reply',
        deleted: 'Comment deleted',
    },
    reports: {
        reason_comment: 'Abusive comment',
        reason_message: 'Abusive message',
    },
    add_comment: 'Add Comment',
    live_chat: 'Live Chat',

    // Login
    login_with_google: 'Continue with Google',
    login_with_facebook: 'Continue with Facebook',
    login_with_microsoft: 'Continue with Microsoft',
    login_with_twitter: 'Continue with X',
    login_welcome: 'Welcome to Dueli',
    login_subtitle: 'Sign in to participate in competitions',
    login_button: 'Login',
    register_button: 'Create Account',
    email_label: 'Email',
    password_label: 'Password',
    name_label: 'Name',
    forgot_password: 'Forgot Password?',
    or_divider: 'Or',
    terms_agreement: 'By continuing, you agree to our Terms and Privacy Policy',
    back_to_login: 'Back to Login',
    reset_password_title: 'Reset Password',
    send_code: 'Send Verification Code',
    verification_code_label: 'Verification Code',
    code_sent_to_email: 'Code sent to your email',
    verify_code: 'Verify Code',
    new_password_label: 'New Password',
    change_password: 'Change Password',
    password_min_length: 'Password must be at least 8 characters',
    or_continue_with: 'Or continue with',

    // User
    user: 'User',
    username: 'Username',
    email: 'Email',
    display_name: 'Display Name',
    bio: 'Bio',
    country: 'Country',
    my_competitions: 'My Competitions',
    my_requests: 'My Requests',

    // Stats
    stats: 'Statistics',
    total_competitions: 'Total Competitions',
    total_wins: 'Wins',
    total_views: 'Views',
    followers: 'Followers',
    following: 'Following',
    // R3-D2 Profile (08/11): SUM stars ÷ contested competitions
    profile_score: 'Overall Rating',
    profile_hint: 'Total stars ÷ competitions contested',
    profile_empty: 'No competitions yet',
    profile_participations: 'competitions',

    // Errors
    error: 'Error',
    not_found: 'Not Found',
    unauthorized: 'Unauthorized',
    forbidden: 'Forbidden',
    server_error: 'Server error. Please try again.',
    login_required: 'Login required',
    no_duels: 'Sorry, no duels available here currently.',
    try_different_filter: 'Try a different filter or create a new competition',
    error_loading: 'Error loading',
    page_not_found: 'Page Not Found',
    back_to_home: 'Back to Home',

    // API Errors (used by controllers)
    errors: {
        missing_fields: 'Missing required fields',
        content_required: 'Content is required',
        invalid_id: 'Invalid ID',
        invalid_rating: 'Rating must be between 1 and 5',
        invalid_request: 'Invalid request',
        fetch_failed: 'Failed to fetch data',
        create_failed: 'Failed to create',
        update_failed: 'Failed to update',
        delete_failed: 'Failed to delete',
        something_wrong: 'Something went wrong. Please try again.',
        blocked_interaction: 'This action is not available',
        rate_limited: 'Too many attempts, please try again shortly',
        content_too_long: 'Text exceeds the allowed length',
        comment_not_found: 'Comment not found',
        not_comment_owner: 'Only the comment owner or an admin can delete it',
        service_unavailable: 'Service temporarily unavailable. Please try again later.',
        session_expired: 'Session expired — refresh the results to see the latest',
    },

    // Discovery (B13: resilient discovery pages)
    discovery: {
        no_results: 'No results to show',
        no_more_results: 'No more results',
        retry: 'Retry',
        session_expired: 'These results expired',
        refresh: 'Refresh',
    },

    // Competition Error Messages
    competition_errors: {
        not_found: 'Competition not found',
        cannot_join_own: 'Cannot join your own competition',
        already_requested: 'You have already requested to join',
        not_completed: 'Competition is not completed',
        already_rated: 'You have already rated this competitor',
        not_eligible_to_start: 'Competition cannot be started in its current state',
        no_opponent: 'Cannot start without an opponent',
        not_live: 'Competition is not live',
        already_completed: 'Competition is already completed',
        vod_url_required: 'Recording URL (vod_url) is required',
        already_has_opponent: 'Competition already has an opponent',
        delete_not_allowed: 'This competition can no longer be deleted',
        invitee_required: 'Invitee is required',
        cannot_invite_self: 'Cannot invite yourself',
        already_invited: 'User already invited',
        no_pending_invitation: 'No pending invitation found',
        no_pending_request: 'No pending request found',
        request_already_accepted: 'Request already accepted',
        already_competitor: 'User is already a competitor in this competition',
        competition_full: 'Competition is full',
        opponent_already_set: 'An opponent has already been set',
        invitation_not_found: 'Invitation not found or expired',
        blocked_user: 'You cannot interact with this user',
        not_authorized: 'You are not authorized for this action',
        // R2-J (H3): mutual/conflicting same-competition paths
        invite_pending_exists: 'You have a pending invitation for this competition — accept or decline it first',
        request_pending_exists: 'This user already has a pending join request for this competition',
        competition_closed: 'This competition is no longer accepting participants',
        rating_self_forbidden: 'You cannot rate yourself or your own competition',
        rating_watch_required: 'Watch 300 seconds of the live broadcast first to unlock rating',
        rating_window_closed: 'The broadcast has ended — ratings are closed and the result is final',
        rating_live_only: 'Ratings are only available during the live broadcast',
        rating_updated: 'Rating update',
        rating_replaced: 'Your rating has been updated',
    },

    // User Error Messages
    user_errors: {
        not_found: 'User not found',
        cannot_follow_self: 'Cannot follow yourself',
    },

    // Category Messages
    category: {
        not_found: 'Category not found',
    },
    success: 'Success',

    // Footer
    footer: '© 2026 Dueli. All rights reserved.',
    about: 'About',
    contact: 'Contact Us',
    terms: 'Terms of Service',
    privacy: 'Privacy Policy',

    // Actions
    view_all: 'View All',
    load_more: 'Load More',
    back: 'Back',
    close: 'Close',
    submit: 'Submit',
    loading: 'Loading...',
    no_more: 'No more results',
    go_home: 'Go to Home',
    go_back: 'Go Back',
    previous: 'Previous',
    next: 'Next',

    // Auth - API messages
    auth_all_fields_required: 'All fields are required',
    auth_email_exists: 'Email already exists',
    auth_register_success: 'Registration successful! Please check your email.',
    auth_register_failed: 'Registration failed',
    auth_invalid_link: 'Invalid or expired verification link',
    auth_account_activated: 'Account activated successfully!',
    auth_email_password_required: 'Email and password are required',
    auth_invalid_credentials: 'Invalid email or password',
    auth_social_no_password: 'This account uses social sign-in (Google/Microsoft/Facebook). Log in with your provider, or reset your password to enable email login.',
    auth_email_is_social: 'This email is registered via social sign-in. Log in with your provider (Google/Microsoft/Facebook) or reset your password.',
    auth_verify_email_first: 'Please verify your email first',
    auth_email_required: 'Email is required',
    auth_user_not_found: 'User not found or already verified',
    auth_verification_resent: 'Verification email sent',
    auth_reset_if_exists: 'If this email is registered, you will receive a reset link',
    auth_reset_code_sent: 'Reset code sent to your email',
    auth_check_spam_folder: 'If you don’t see the email, check your Spam or Junk folder.',
    auth_email_code_required: 'Email and code are required',
    auth_invalid_code: 'Invalid or expired code',
    auth_code_verified: 'Code verified',
    auth_password_required: 'New password is required',
    auth_password_changed: 'Password changed successfully',
    auth_email_not_verified: 'Please verify your email before logging in',
    auth_invalid_token: 'Invalid or expired token',
    auth_email_verified: 'Email verified successfully! You can now log in.',
    auth_connection_failed: 'Connection failed. Please try again.',

    // Verification
    verification_failed: 'Verification failed',
    verification_invalid_link: 'Invalid link',
    verification_success_msg: 'You can now log into your account',
    verification_check_link: 'Please check the link or request a new one',
    account_verification: 'Account Verification',

    // About page
    about_title: 'Dueli Competition Platform',
    about_description: 'The first platform of its kind combining live competitions, constructive dialogues, and talent discovery in a fair interactive environment.',
    about_live_streaming: 'Live Streaming & Interaction',
    about_live_streaming_desc: 'Advanced streaming system bringing competitors side-by-side with audience interaction and live voting.',
    about_fair_judging: 'Fair Judging System',
    about_fair_judging_desc: 'Transparent judging mechanisms based on audience voting and expert panels to ensure fairness.',
    about_global_community: 'Global Community',
    about_global_community_desc: 'Connect with creators and thinkers from around the world and participate in cross-border competitions.',
    about_platform_preview: 'Platform Preview',
    about_developed_by: 'Developed by Maelsh',
    about_maelsh_desc: 'At Maelsh, we believe in the power of dialogue and fair competition in building communities. We strive to provide innovative software solutions that combine aesthetics and functionality to serve the Arab and global user.',
    about_dueli: 'About Dueli',

    // Competition page
    competitors: 'Competitors',
    competition_share: {
        share_button: 'Share competition',
        dialog_title: 'Share this competition',
        copy_link: 'Copy link',
        copy_success: 'Link copied',
        copy_failed: 'Could not copy the link',
        close: 'Close',
        platform_whatsapp: 'WhatsApp',
        platform_telegram: 'Telegram',
        platform_facebook: 'Facebook',
        platform_x: 'X',
        platform_linkedin: 'LinkedIn',
        platform_email: 'Email',
    },
    awaiting_opponent: 'Awaiting Opponent',
    login_to_compete: 'Login to Compete',
    stream_not_available: 'Stream not available',
    no_comments_yet: 'No comments yet',
    new_follower: 'New Follower',

    // Email
    email_activate_subject: 'Activate your Dueli account',
    email_reset_subject: 'Reset Your Password',

    // Notifications
    notification: {
        new_join_request: 'New Join Request',
        join_request_for: 'Join request for competition',
        request_accepted: 'Request Accepted',
        request_accepted_message: 'Your request to join the competition has been accepted',
        new_message: 'New Message',
        new_message_body: '{actor}: {preview}',
        // R2-M: official admin reply — distinct label from personal messages.
        new_admin_message: 'New message from Dueli Support',
        new_admin_message_body: '{preview}',
        new_post_like: 'New Like on Your Post',
        new_post_like_body: '{actor} liked your post',
        new_post_comment: 'New Comment on Your Post',
        new_post_comment_body: '{actor} commented on your post',
        new_comment: 'New Comment',
        new_comment_body: '{actor} commented on the competition',
        new_join_request_body: '{actor}: {preview}',
        competition_invite: 'Competition Invitation',
        competition_invite_body: '{actor}: {preview}',
        // R2-J: invite-acceptance label — distinct from new_join_request
        invitation_accepted: 'Invitation Accepted',
        new_rating: 'New Rating',
        new_rating_body: '{actor} rated the competition',
        system_notice: 'System notice',
        generic: 'You have a new notification',
        // R4-EVENTS-NOTIFY-1 (N-05/M-5): dropdown star control labels.
        star: 'Star',
        unstar: 'Unstar',
        // F-7: Competition lifecycle notifications — every user-facing string via i18n
        comp_deleted_title: 'Competition Deleted',
        comp_deleted: 'Your instant competition was deleted because no opponent joined within 1 hour.',
        comp_cancelled: 'Competition Cancelled',
        comp_cancelled_no_opponent: 'Your scheduled competition was cancelled because no opponent joined within 1 hour of the scheduled time.',
        comp_cancelled_not_started: 'The scheduled competition was cancelled because it did not start within 1 hour of the scheduled time.',
        comp_cancelled_generic: 'The competition was cancelled because it did not start in time.',
        comp_ended_title: 'Competition Ended',
        comp_ended: 'The live competition was automatically ended after reaching the 2-hour maximum duration.',
        comp_reminder_title: 'Competition Reminder',
        comp_reminder: 'The competition will start soon!',
    },

    // Messages
    messages: {
        title: 'Messages',
        select_conversation: 'Select a conversation',
        no_conversations: 'No conversations yet',
        no_messages: 'No messages yet',
        type_message: 'Type a message...',
        enter_message: 'Enter your message',
        back_to_conversations: 'Back to chats',
        back_to_admin_messages: 'Back to admin messages',
        send: 'Send',
        send_failed: 'Could not send the message. Try again.',
        sending: 'Sending...',
        composer_hint: 'Enter to send · Shift+Enter for a new line',
        new_conversation: 'New conversation',
        load_failed: 'Could not load messages',
    },

    // R2-M (H6): support threads / admin inbox
    support: {
        personal_tab: 'Chats',
        admin_tab: 'Admin messages',
        official_sender: 'Dueli Support',
        new_thread: 'New message to admin',
        subject_label: 'Subject (optional)',
        no_threads: 'No admin messages yet',
        select_thread: 'Select a message',
        thread_closed: 'This thread is closed',
        admin_inbox: 'Support inbox',
        reply_label: 'Reply officially',
        close_thread: 'Close thread',
        reopen_thread: 'Reopen thread',
        unread_admin: 'unread admin messages',
    },

    // Message error keys (used by MessageController)
    message: {
        invalid_recipient: 'Invalid recipient',
        content_required: 'Message content is required',
    },

    // Settings Page
    settings_page: {
        title: 'Settings',
        language: 'Default Language',
        country: 'Default Country',
        privacy: 'Profile Privacy',
        privacy_public: 'Public',
        privacy_followers: 'Followers Only',
        privacy_private: 'Private',
        notifications: 'Enable Notifications',
        email_notifications: 'Email Notifications',
        push_notifications: 'Push Notifications',
        save: 'Save Settings',
        saved: 'Settings saved successfully',
        password_section: 'Password',
        password_current: 'Current password',
        password_new: 'New password',
        password_confirm: 'Confirm new password',
        password_change_submit: 'Update password',
        password_confirm_mismatch: 'New password and confirmation do not match',
        password_social_hint: 'This account uses social sign-in. Log in with your provider, or use password reset to enable email login.',
        password_reset_cta: 'Reset password via email',
    },
    profile_competitions: {
        load_more: 'Load more competitions',
        delete_action: 'Delete competition',
        delete_confirm_title: 'Delete this competition?',
        delete_confirm_body: '“{title}” will be permanently deleted. This cannot be undone.',
        delete_success: 'Competition deleted',
        delete_failed: 'Could not delete competition',
        delete_in_progress: 'Deleting…',
    },

    // Reports
    report: {
        title: 'Report',
        reason: 'Reason',
        select_reason: 'Select a reason',
        target_type: 'Report about',
        select_target_type: 'Select what to report',
        target_id: 'Target ID',
        target_id_placeholder: 'User, competition or comment ID',
        description: 'Additional Details',
        description_placeholder: 'Provide more details about the issue...',
        submit: 'Submit Report',
        submitted: 'Report submitted successfully',
        already_reported: 'You have already reported this',
        reason_spam: 'Spam',
        reason_harassment: 'Harassment',
        reason_fake_account: 'Fake Account',
        reason_inappropriate_content: 'Inappropriate Content',
        reason_misleading: 'Misleading',
        reason_copyright: 'Copyright Violation',
        reason_hate_speech: 'Hate Speech',
        reason_other: 'Other',
    },

    // Schedule
    schedule: {
        title: 'My Schedule',
        reminders: 'Reminders',
        upcoming: 'Upcoming Competitions',
        empty: 'No scheduled competitions',
        creator: 'Creator',
        add_reminder: 'Set Reminder',
        remove_reminder: 'Remove Reminder',
        reminder_set: 'Reminder set!',
        reminder_removed: 'Reminder removed',
        not_scheduled: 'Competition is not scheduled',
        not_eligible: 'Reminders are not available for this competition status',
        already_set: 'Reminder already set',
        not_found: 'Reminder not found',
    },

    // Likes
    like: {
        title: 'Likes',
        already_liked: 'Already liked',
        not_found: 'Like not found',
    },

    // Dislikes (B8)
    interactions: {
        dislike: 'Dislike',
        undislike: 'Remove dislike',
        dislikes_count: 'Dislikes',
        dislike_not_found: 'Dislike not found',
    },

    // Posts
    post: {
        content_required: 'Post content is required',
        created: 'Post created',
        deleted: 'Post deleted',
    },

    // Auth (nested for client-side usage)
    auth: {
        login_success: 'Login successful!',
        logout_success: 'Logged out successfully',
        connection_failed: 'Connection failed. Please try again.',
    },

    // Client-side toasts
    client: {
        toast: {
            welcome: 'Welcome back!',
            allow_popups: 'Please allow popups for OAuth login',
        },
    },

    // Search
    search: {
        no_results: 'No results found',
    },

    // R3-D1 (h7-v1): explicit interest favorites (optional Settings choice)
    favorites: {
        title: 'Favorite sections',
        hint: 'Pick sections and branches you care about to improve recommendations (optional)',
        saved: 'Favorites saved',
        empty: 'No favorites yet — you will see general recommendations',
    },
    similar: {
        title: 'Similar competitions',
    },

    // UNIT32.1 (R4-PROFILE-COLLECTIONS-1): profile owner collections
    watch_collections: {
        title: 'My collections',
        hint: 'Your saved competitions and viewing history — visible only to you.',
        tab_later: 'Watch Later',
        tab_history: 'Viewing History',
        add: 'Save to Watch Later',
        remove: 'Remove from Watch Later',
        added: 'Added to Watch Later',
        removed: 'Removed from Watch Later',
        save_failed: 'Could not update Watch Later. Please try again.',
        loading: 'Loading…',
        empty_later: 'You have not saved any competition yet. Use the bookmark icon on any card.',
        empty_history: 'No viewing history yet. Competitions you actually watch appear here.',
        error: 'Could not load this list.',
        retry: 'Retry',
        load_more: 'Load more',
        history_hint: 'Newest first — only real playback is recorded.',
        watched_for: 'Watched for',
        less_than_minute: 'Less than a minute',
        minutes_short: 'min',
    },

    // Profile page (new)
    wins: 'Wins',
    posts: 'Posts',
    no_competitions: 'No competitions yet',
    no_posts: 'No posts yet',
    edit_profile: 'Edit Profile',
    send_message: 'Message',

    // Competition status (new)
    completed: 'Completed',
    view: 'View',
    confirm_delete: 'Are you sure you want to delete?',

    // Requests (new)
    received_requests: 'Received',
    sent_requests: 'Sent',
    no_requests: 'No requests yet',
    wants_to_join: 'Wants to join your competition',
    you_requested: 'You requested to join',
    invitations: 'Invitations',
    invites_you: 'Invites you to compete',

    // Interaction buttons (new)
    remind_me: 'Remind Me',
    reminder_added: 'Reminder added!',
    report_inappropriate: 'Inappropriate content',
    report_spam: 'Spam or misleading',
    report_harassment: 'Harassment or bullying',
    report_submitted: 'Report submitted. Thank you!',
    select_reason: 'Please select a reason',

    // Jitsi / Live (new)
    jitsi_not_configured: 'Live streaming is not configured yet.',
    confirm_end_call: 'Are you sure you want to end the call?',

    // Live Streaming (P2P)
    recording: 'Recording',
    toggle_mic: 'Mute/Unmute Mic',
    toggle_speaker: 'Mute/Unmute Sound',
    toggle_camera: 'Turn Camera On/Off',
    share_screen: 'Share Screen',
    end_stream: 'End Stream',
    end_stream_confirm: 'Are you sure you want to end the stream?',
    join_stream: 'Join Stream',
    you_are_host: 'You are the host',
    you_are_guest: 'You are the guest',
    end: 'End',
    you: 'You',
    opponent: 'Opponent',
    waiting_opponent: 'Waiting for opponent...',
    chunks_uploaded: 'chunks uploaded',
    camera_error: 'Failed to access camera/microphone',
    hls_not_supported: 'Your browser does not support HLS playback',
    return_to_live: 'Back to live',
    live_rewind: 'Rewind one chunk',
    // R4-CHUNK-VIEW-RECOVERY-1 REM1 (28.4.1): audience player states + replay
    replay: 'Replay',
    live_timeline: 'Live timeline',
    player_state_waiting: 'Waiting for stream...',
    player_state_playing: 'Playing',
    player_state_processing: 'Preparing the recording...',
    player_state_error: 'Media could not be played',
    player_state_ready: 'Ready to play',
    player_state_unavailable: 'Media unavailable',
    live_comments: 'Live comments will appear here...',
    fullscreen: 'Fullscreen',
    exit_fullscreen: 'Exit Fullscreen',
    swap_videos: 'Swap Videos',
    toggle_comments: 'Toggle Comments',
    toggle_my_video: 'Toggle My Video',
    switch_camera: 'Switch Camera',
    close_ad: 'Close Ad',
    report_ad: 'Report Ad',
    ad: 'Ad',
    clear_data: 'Clear Site Data',
    viewers_cannot_close_ads: 'Viewers cannot close ads',
    report_ad_confirm: 'Report this ad as inappropriate?',
    ad_reported: 'Ad reported. Thank you!',
    // R4-LIVE-ENTRY-RECOVERY-1: private prep & explicit consent gate
    prep_privacy_hint: 'Your audio/video is not sent until you press the publish button. This is a local preview only.',
    prep_publish_host: 'Start Broadcast',
    prep_publish_guest: 'Publish & Join',
    prep_publishing: 'Publishing...',
    prep_starting: 'Starting the competition...',
    prep_ending: 'Ending the competition...',
    prep_verifying_end: 'Verifying the end result...',
    end_competition_failed: 'Could not end the competition. It is still available to retry.',
    video_layout_waiting: 'Waiting for opponent video — your local preview is large',
    video_layout_remote: 'Opponent video is large — your preview is in the small window',
    video_layout_lost: 'Opponent video stopped — your preview is large again',
    video_layout_terminal: 'Competition ended',
    prep_published: 'Published — media is now live',
    prep_waiting_host: 'Waiting for opponent to publish...',
    prep_waiting_guest: 'Waiting for host to start...',
    // R4-LIVE-ENTRY-RECOVERY-REM1: honest prep/publish + leave/stage controls
    live_tools: 'Broadcast tools',
    start_competition: 'Start competition',
    start_competition_failed: 'Could not start the competition',
    publish_state_off: 'Your media is not published — local preview only',
    publish_state_on: 'You are publishing — the other party receives your media',
    nothing_to_publish: 'Nothing to publish. Allow camera/microphone or share your screen first.',
    media_none: 'No camera or microphone. You can share your screen to prepare.',
    waiting_peer_media: 'Waiting for the other party media...',
    screen_share_stopped: 'Screen sharing stopped.',
    end_competition: 'End competition',
    end_competition_confirm: 'End the competition for everyone?',
    early_end_confirm: 'Early end: you will be treated as withdrawn and lose the competition. Your opponent wins by withdrawal and receives a SYSTEM credit of 10 stars (two five-star ratings); you receive 0, and the short competition quality is 0. Continue?',
    early_end_winner_banner: 'Winner by early withdrawal',
    early_end_result_note: 'System credit, separate from audience votes: 10 stars to the opponent and 0 to the ending competitor.',
    fullscreen_unavailable: 'Fullscreen is unavailable here; the in-page expanded view is being used instead.',
    expand_stage: 'Expand stage',
    collapse_stage: 'Collapse stage',

    // Navigation (new)
    notifications: 'Notifications',
    mark_all_read: 'Mark all read',
    no_messages: 'No messages',
    earnings_nav: 'Earnings',
    donate: 'Support',
    contact_admin: 'Contact Admin',
    submit_report: 'Submit Report',
    no_notifications: 'No notifications',

    // Notifications page (post-R1 acceptance: the /notifications route)
    notifications_page: {
        mark_read: 'Mark read',
    },

    // Earnings Page
    available: 'Available',
    pending: 'Pending',
    on_hold: 'On Hold',
    ad_revenue: 'Ad Revenue',
    total_earnings: 'Total Earnings',
    from_competitions: 'From competitions',
    withdrawn: 'Withdrawn',
    to_bank: 'To bank account',
    withdraw: 'Withdraw',
    min_withdrawal: 'Minimum withdrawal: $50.00',
    request_withdrawal: 'Request Withdrawal',
    withdrawal_history: 'Withdrawal History',
    no_withdrawal_history: 'No withdrawal history yet',
    submit_withdrawal: 'Submit Withdrawal Request',
    withdrawal_submitted: 'Withdrawal request submitted!',
    withdrawal_approved: 'Withdrawal request approved!',
    withdrawal_rejected: 'Withdrawal rejected: ',
    withdrawal_cancelled: 'Withdrawal cancelled and funds refunded.',
    confirm_cancel_withdrawal: 'Cancel this withdrawal request? Your balance will be refunded.',
    amount: 'Amount',
    payment_method: 'Payment Method',
    bank_transfer: 'Bank Transfer',
    payment_details: 'Payment Details',
    payment_details_placeholder: 'IBAN / Wise email / Wallet address...',
    payment_details_required: 'Payment details are required.',
    processing: 'Processing...',
    network_error: 'Network error. Please try again.',
    // Nested earnings keys for earnings page components
    earnings: {
        total: 'Total',
        pending: 'Pending',
        per_competition: 'Per Competition',
    },

    // Wallet (ledger 8.A)
    wallet: {
        balance: 'Wallet balance',
        insufficient_funds: 'Insufficient funds for this transaction',
        transaction_failed: 'Wallet transaction failed — please try again',
    },

    // Payments — Stripe ledger integration (8.C)
    payment_min_amount: 'Minimum donation amount is $1',
    payment_method_invalid: 'Invalid payment method',
    payment_failed: 'Payment initialization failed — please try again later',
    payment_success: 'Payment completed successfully',
    payment_cancelled: 'Payment was cancelled',
    donation_completed: 'Thank you — your donation was completed',
    donation_failed: 'Your donation did not go through',
    donation_refunded: 'This donation was refunded',

    // Withdrawals — documented lifecycle (8.D): requested → approved → paid | rejected
    withdrawals: {
        requested: 'New withdrawal request pending admin review',
        approved: 'Withdrawal request approved by admin',
        rejected: 'Withdrawal request rejected and the held amount returned to your balance',
        min_amount: 'Minimum withdrawal amount is $50',
        insufficient_balance: 'Your available balance does not cover the requested amount',
    },

    // R2-P: saved payout methods + withdrawal snapshots
    payout: {
        type_invalid: 'Valid type required: bank, paypal, or wise',
        bank_fields_required: 'IBAN and bank name are required for bank accounts',
        email_required: 'Email is required for PayPal/Wise',
        method_not_found: 'Payout method not found',
        saved_methods: 'Saved payout methods',
        use_saved_method: 'Use a saved method',
        manual_entry: 'Enter details manually',
        no_saved_methods: 'No saved payout methods yet',
    },

    // Competitor donations (8.E) — $1 documented minimum, no Dueli-level maximum
    // 8.G-F3 — donations are non-refundable once completed (see docs/02-DATABASE.md)
    donations: {
        send: 'Send donation',
        thanks: 'Thanks for supporting the competitor!',
        min: 'Minimum donation is $1',
        max: 'Dueli sets no maximum donation amount',
        blocked: 'You cannot donate to a user who blocked you',
        invalid_competition: 'Live donations require a live competition and a competitor in it',
        non_refundable: 'Donations are non-refundable once completed',
        non_refundable_accept: 'I understand and accept that this donation is non-refundable',
        non_refundable_required: 'You must accept that donations are non-refundable',
        amount_confirm: 'I confirm this donation amount',
        amount_confirm_required: 'You must confirm the donation amount',
        be_first: 'Be the first supporter!',
        my_donations: 'My Donations',
        my_donations_empty: 'You have not donated yet',
        my_donations_error: 'Could not load your donations',
    },

    // Reports Page
    report_type: 'Report Type',
    report_inappropriate_desc: 'Offensive or harmful content',
    report_spam_desc: 'Fake or deceptive content',
    report_harassment_desc: 'Targeting or attacking others',
    other: 'Other',
    other_desc: 'Something else',
    subject: 'Subject',
    description: 'Description',
    enter_subject: 'Enter subject',
    describe_issue: 'Please describe the issue in detail',

    // Donate Page
    support_dueli: 'Support Dueli',
    support_message: 'Help us build a better platform for meaningful conversations and connections.',
    coffee: 'Buy us a coffee',
    supporter: 'Supporter',
    champion: 'Champion',
    custom_amount: 'Or enter a custom amount',
    donate_now: 'Donate Now',
    popular: 'Popular',
    top_supporters: 'Top Supporters',

    // Test Stream Pages
    test_stream: 'Test Stream',
    test_stream_desc: 'Test Stream - WebRTC + Canvas + Upload',
    host: 'Host',
    host_desc: 'Starts the stream and shares screen',
    guest: 'Guest',
    guest_desc: 'Joins the stream as competitor',
    viewer: 'Viewer',
    viewer_desc: 'Watches the stream live or recorded',
    screen_share: 'Screen Share',
    canvas_recording: 'Canvas Recording',
    new_chunk: 'New Chunk',
    upload_chunks: 'Upload Chunks',
    adaptive_streaming: 'Adaptive Streaming',
    notes: 'Notes',
    test_note_1: 'All three pages use shared core.ts',
    test_note_2: 'Host records Canvas and uploads chunks',
    test_note_3: 'Guest shares screen and answers Offer',
    test_note_4: 'Viewer uses smart player with Adaptive polling',
    competition_number: 'Competition Number',
    enter_number: 'Enter number',
    initializing: 'Initializing...',
    enter_comp_id_to_join: 'Enter competition number then press Join',
    share_then_join: 'Press "Share Screen" then "Join"',
    quality: 'Quality',
    determining: 'Determining...',
    waiting: 'Waiting...',
    mode: 'Mode',
    chunks: 'Chunks',
    buffer: 'Buffer',
    enter_comp_to_watch: 'Enter competition number to start watching',
    smart_viewer: 'Smart viewer: HLS first, MSE fallback',
    start_watching: 'Watch',
    stop_watching: 'Stop',
    reconnect: 'Reconnect',
    reconnecting: 'Restoring connection...',
    reconnected: 'Connection restored',
    reconnect_failed: 'Could not restore the connection',
    connect: 'Connect',
    join: 'Join',
    disconnect: 'Disconnect',
    download: 'Download',
    converting: 'Converting',
    page_loaded: 'Page loaded',

    // R4-CHUNK-DOWNLOAD-1: independent MP4 recording download
    download_ui: {
        title: 'Download the recording',
        close: 'Close',
        start: 'Download MP4',
        start_partial: 'Download available clip',
        cancel: 'Cancel',
        retry: 'Retry',
        save_again: 'Save again',
        loading_manifest: 'Reading the authoritative recording manifest…',
        analyzing: 'Checking chunks, duration and available memory…',
        complete_label: 'Complete recording',
        partial_label: 'Partial clip',
        rights_competitor: 'You are a competitor in this competition. You may republish and monetize your competition material.',
        rights_viewer: 'You need permission from the platform to republish this recording. Personal viewing is allowed.',
        meta_size: 'Size',
        meta_duration: 'Duration',
        meta_chunks: 'Chunks',
        meta_mode: 'Processing',
        mode_mp4_remux: 'MP4 remux (no re-encode)',
        mode_ffmpeg: 'MP4 conversion (H.264/AAC)',
        mode_none: 'Unavailable',
        partial_notice: 'This recording is not complete/finalized. You can download the currently available ready chunks as a clearly labelled partial clip.',
        resource_note: 'Processing runs entirely in your browser. The browser build of FFmpeg has a measured 2 GiB WebAssembly heap ceiling and also needs memory for the input chunks and the output file. Very long recordings may exceed the device’s available memory; the app will stop with a clear message instead of producing an incomplete file.',
        progress_fetching: 'Downloading chunks',
        progress_preparing: 'Preparing the converter',
        progress_assembling: 'Assembling MP4',
        progress_converting: 'Converting to MP4',
        progress_finalizing: 'Verifying the file',
        progress_done: 'Done',
        success: 'Your MP4 is ready: {size}, {duration}.',
        success_partial: 'Your partial MP4 is ready: {size}, {duration}. It is not the complete recording.',
        cancelled: 'The download was cancelled. No file was saved.',
        cancelled_cleanup: 'Temporary chunks, worker memory and object URLs were released.',
        error_manifest: 'The download manifest could not be loaded.',
        error_not_authenticated: 'Sign in to download the recording.',
        error_no_chunks: 'No ready recording chunks are available yet.',
        error_not_finalized: 'The recording has not been finalized yet.',
        error_unsupported_format: 'The recording format cannot be downloaded.',
        error_duration_unknown: 'A verified duration is missing, so a valid MP4 cannot be produced.',
        error_truncated: 'The recording manifest exceeded the safe read bound.',
        error_heap_limit: 'The estimated processing memory exceeds the browser FFmpeg heap limit.',
        error_chunk_fetch: 'A recording chunk could not be downloaded after retries.',
        error_chunk_integrity: 'A recording chunk failed its size or checksum verification.',
        error_assembly: 'The MP4 structure could not be assembled safely.',
        error_ffmpeg_unavailable: 'The self-hosted FFmpeg worker could not be loaded. Check your connection and Content-Security-Policy support, then retry.',
        error_ffmpeg: 'The browser converter failed before producing a valid MP4.',
        error_output_validation: 'The produced MP4 failed playback/duration/seek verification, so it was not saved.',
        error_unknown: 'The download failed for an unexpected reason.',
        error_detail: 'Technical detail',
        aria_dialog: 'Recording download',
        aria_progress: 'Download progress',
        language_note: 'The file is a standard MP4 (H.264/AAC when conversion is required).',
    },

    // ── Task 4: Transparency Engine ────────────────────────────
    transparency: {
        // 8.F: ledger-backed money transparency (public totals + verification)
        total_in: 'Total In',
        total_out: 'Total Out',
        platform_share: 'Platform Share',
        verified_at: 'Verified At',
        page_title: 'Transparency Ledger',
        page_subtitle: 'An open-book view of every dollar that flows through Dueli.',
        nav_link: 'Transparency',
        hero_badge: 'Open-Book Policy',
        last_audit: 'Last Audit',
        audit_balanced: 'Ledger Balanced ✓',
        audit_imbalanced: 'Ledger Discrepancy Detected ⚠',
        audit_discrepancy: 'Discrepancy',

        kpi_total_revenue: 'Total Ad Revenue',
        kpi_platform_share: 'Platform Share Retained',
        kpi_competitors_paid: 'Competitors Paid',
        kpi_operating_costs: 'Operating Costs',
        kpi_donations_in: 'Donations Received',
        kpi_net_platform: 'Platform Net (Today)',

        section_equation: 'Accounting Equation',
        section_daily: 'Daily Financial Trend',
        section_financial_feed: 'Financial Log Feed',
        section_donation_feed: 'Donations Ledger',
        section_payroll: 'Payroll Breakdown',
        section_admin_log: 'Admin Interventions',
        section_top_donors: 'Top Donors Hall of Fame',

        eq_ad_revenue: 'Ad Revenue',
        eq_minus: '−',
        eq_operating_costs: 'Operating Costs',
        eq_minus_donors: '− Donor Funding',
        eq_equals: '=',
        eq_platform_net: 'Platform Net',
        eq_plus: '+',
        eq_competitor_payouts: 'Competitor Payouts',

        th_date: 'Date',
        th_type: 'Entry Type',
        th_amount: 'Amount (USD)',
        th_description: 'Description',
        th_role: 'Role',
        th_action: 'Action',
        th_target: 'Target',
        th_donor: 'Donor',
        th_total_donated: 'Total Donated',
        th_entries: 'Entries',

        type_ad_revenue: 'Ad Revenue',
        type_competitor_payout: 'Competitor Payout',
        type_platform_share: 'Platform Share',
        type_server_cost: 'Server Cost',
        type_developer_salary: 'Developer Salary',
        type_admin_salary: 'Admin Salary',
        type_donor_allocation: 'Donor Allocation',
        type_other_cost: 'Other Cost',
        type_donation_received: 'Donation Received',
        type_donation_allocated: 'Donation Allocated',

        load_more: 'Load More Entries',
        no_entries: 'No entries yet.',
        anonymous_donor: 'Anonymous',
        verified_label: 'Verified Open-Book Platform',
        updated_live: 'Data updated live from ledger',
        payroll_note: 'Individual payroll details are kept confidential. Aggregate figures only.',
        admin_note: 'Admin identities are anonymized. Role labels only.',
    },

    // ── Task 1: Advertiser Portal ────────────────────────────
    advertiser: {
        portal_title: 'Advertiser Portal',
        dashboard: 'Dashboard',
        my_campaigns: 'My Campaigns',
        create_campaign: 'Create Campaign',
        campaign_title: 'Campaign Title',
        budget: 'Budget',
        budget_remaining: 'Budget Remaining',
        target_language: 'Target Language',
        target_country: 'Target Country',
        revenue_per_view: 'Revenue Per View',
        impressions: 'Impressions',
        clicks: 'Clicks',
        spend: 'Total Spend',
        ctr: 'CTR',
        status_active: 'Active',
        status_paused: 'Paused',
        status_depleted: 'Depleted',
        status_archived: 'Archived',
        pause_campaign: 'Pause Campaign',
        resume_campaign: 'Resume Campaign',
        analytics: 'Analytics',
        total_budget: 'Total Budget',
        total_remaining: 'Total Remaining',
        active_campaigns: 'Active Campaigns',
        no_campaigns: 'No campaigns yet. Create your first campaign!',
        campaign_created: 'Campaign created successfully',
        campaign_paused: 'Campaign paused',
        campaign_resumed: 'Campaign resumed',
        budget_exhausted: 'Budget exhausted',
        media_url: 'Media URL',
        media_kind: 'Media type',
        media_image: 'Image',
        media_gif: 'Animated GIF',
        media_video: 'Video',
        duration_sec: 'Video duration (seconds)',
        duration_hint: 'e.g. 15',
        image_url: 'Image URL',
        link_url: 'Link URL',
        budget_cents_label: 'Budget (cents)',
        cost_cents_label: 'Cost per impression (cents)',
        submitted_review: 'Campaign submitted for review',
        campaign_ended: 'Campaign ended',
        submit_for_review: 'Submit for review',
        end_campaign: 'End campaign',
        not_your_campaign: 'This campaign belongs to another advertiser',
    },

    // ── Phase 9.A: Ad campaign lifecycle ──────────────────────
    ads: {
        campaign_status_draft: 'Draft',
        campaign_status_pending_review: 'Pending Review',
        campaign_status_active: 'Active',
        campaign_status_paused: 'Paused',
        campaign_status_ended: 'Ended',
        budget_exhausted: 'Campaign budget exhausted — serving stopped automatically',
        pending_review: 'Campaign is pending admin review and cannot be activated yet',
        invalid_transition: 'Invalid campaign status transition',
        invalid_budget: 'Budget must be a positive integer amount in cents',
        campaign_not_draft: 'Campaign must be in draft to submit it for review',
        campaign_not_active: 'Campaign must be active for this action',
        campaign_not_paused: 'Campaign must be paused to resume it',
        // ── Phase 9.C: metrics and anti-fraud ─────────────────
        impressions: 'Impressions',
        clicks: 'Clicks',
        ctr: 'CTR',
        spend: 'Spend',
        // ── Phase 9.B: ad serving and targeting ───────────────
        sponsored_label: 'Sponsored',
        why_this_ad: 'Why am I seeing this ad?',
        hide_ad: 'Hide ad',
        frequency_cap_reached: 'You have reached the display frequency limit for this ad',
        policy_title: 'Competition ad control',
        policy_hint: 'Choose how third-party ads appear in your competitions. Dueli house ads cannot be blocked.',
        policy_auto: 'Automatic',
        policy_allow: 'Allow list',
        policy_manual: 'Manual approval',
        policy_block: 'Block third-party ads',
        policy_invalid: 'Invalid ad setting',
        policy_saved: 'Ad preference saved',
        policy_grants_title: 'Approved ads',
        policy_grants_hint: 'In manual-approval mode only ads you approve here appear. Dueli house ads never need approval.',
        policy_grants_empty: 'No eligible paid ads to approve right now',
        policy_grant: 'Approve',
        policy_revoke: 'Revoke approval',
        policy_loading: 'Loading...',
        house_limit: 'At most 3 active house ads',
        invalid_media: 'Media type must be image, GIF, or video',
        invalid_duration: 'Video duration must be a positive integer number of seconds',
    },

    // ── C7: synthetic/demo data labeling ────────────────────
    demo: {
        badge: 'Demo',
        title: 'Demo data for illustration — not a real account or competition',
    },

    // ── Task 2: Admin Governance ──────────────────────────────
    admin: {
        dashboard_title: 'Admin Dashboard',
        enhanced_stats: 'Enhanced Statistics',
        admin_roles: 'Admin Roles',
        grant_role: 'Grant Role',
        revoke_role: 'Revoke Role',
        role_superadmin: 'SuperAdmin',
        role_auditor: 'Auditor',
        role_moderator: 'Moderator',
        invalid_role: 'Invalid role specified',
        audit_logs: 'Audit Logs',
        platform_settings: 'Platform Settings',
        setting_key: 'Setting Key',
        setting_value: 'Setting Value',
        update_setting: 'Update Setting',
        active_users: 'Active Users',
        demographics: 'User Demographics',
        hottest_competitions: 'Hottest Competitions',
        financial_summary: 'Financial Summary',
        today_summary: "Today's Summary",
        pending_arbitrations: 'Pending Arbitrations',
        active_campaigns: 'Active Ad Campaigns',
        competitions_by_status: 'Competitions by Status',
        action_grant_role: 'Grant Role',
        action_revoke_role: 'Revoke Role',
        action_update_setting: 'Update Setting',
        action_arbitration: 'Arbitration Action',
        action_finalize_payout: 'Finalize Payout',
        role_not_found: 'Admin role not found',
        last_superadmin: 'Cannot revoke the last SuperAdmin',
        access_denied: 'Admin access required',
        admin_panel: 'Admin Panel',
        my_account: 'My Account',
        ad_pricing_title: 'Ad pricing',
        ad_pricing_hint: 'Leave fields empty to keep each campaign current cost. Every unset multiplier is 1.',
        country_base_cents: 'Country base price (cents)',
        country_code: 'Country code',
        base_cents: 'Cents',
        h7_multiplier: 'H7 multiplier',
        targeting_multiplier: 'Targeting multiplier',
        media_multipliers: 'Media multipliers',
        duration_multipliers: 'Video duration multipliers (seconds)',
        duration_seconds: 'Seconds',
        multiplier: 'Multiplier',
        add_row: 'Add row',
        remove_row: 'Remove row',
        save_pricing: 'Save pricing',
        clear_pricing: 'Clear pricing',
        pricing_saved: 'Pricing saved',
        pricing_clear_confirm: 'Clear every pricing setting and restore campaign cost?',
        house_title: 'Dueli house ad',
        house_hint: 'Serves as fallback when no eligible paid ad exists. At most 3 active house ads; they can never be blocked.',
        house_ad_title: 'Ad title',
        house_create: 'Create house ad',
        house_created: 'House ad created',
        media_url: 'Media URL',
        media_kind: 'Media type',
        media_image: 'Image',
        media_gif: 'GIF',
        media_video: 'Video',
        duration_sec: 'Duration (seconds)',
        link_url: 'Link URL',
        save: 'Save',
        documents_title: 'Documents & Data',
        documents_empty: 'No documents yet',
    },

    // ── R2-A: self-service account settings ──────────────
    account: {
        username_updated: 'Username updated',
        email_updated: 'Email updated — please verify the new address',
        password_updated: 'Password updated — please log in again',
        username_taken: 'Username is already taken',
        username_invalid: 'Username must be 3-30 lowercase letters, digits, dots or underscores',
        email_taken: 'Email is already in use',
        email_invalid: 'Invalid email address',
        current_password_incorrect: 'Current password is incorrect',
    },

    // ── R2-A (H9): managed documents/data ──────────────
    documents: {
        slug_invalid: 'Slug must be 2-80 lowercase letters, digits or dashes',
        slug_taken: 'A document with this slug already exists',
        status_invalid: 'Status must be draft or published',
        visibility_invalid: 'Visibility must be private or public',
        created: 'Document created',
        updated: 'Document updated',
        deleted: 'Document deleted',
        draft: 'Draft',
        published: 'Published',
        private: 'Private',
        public: 'Public',
        preview: 'Preview',
        save: 'Save',
        seed_badge: 'Test seed data',
    },

    // ── Task 3: Arbitration & Complaints ──────────────────────
    arbitration: {
        submit_complaint: 'Submit Complaint',
        my_complaints: 'My Complaints',
        track_complaint: 'Track Complaint',
        complaint_submitted: 'Complaint submitted successfully',
        status_submitted: 'Submitted',
        status_under_review: 'Under Review',
        status_investigation: 'Investigation',
        status_resolved: 'Resolved',
        status_rejected: 'Rejected',
        assigned_admin: 'Assigned Admin',
        admin_role: 'Admin Role',
        state_history: 'State History',
        transition_notes: 'Transition Notes',
        no_complaints: 'You have not submitted any complaints',
        complaint_not_found: 'Complaint not found',
        invalid_transition: 'Invalid state transition',
        assign_to_admin: 'Assign to Admin',
        transition_state: 'Transition State',
        resolve: 'Resolve',
        reject: 'Reject',
        investigate: 'Start Investigation',
    },

    // ── Task 5: Live Finance Engine ──────────────────────────
    live_finance: {
        dashboard_title: 'Live Finance Dashboard',
        total_ad_revenue: 'Total Ad Revenue',
        platform_cut: 'Platform Cut',
        creator_payout: 'Creator Payout',
        opponent_payout: 'Opponent Payout',
        competitor_pool: 'Competitor Pool',
        platform_percentage: 'Platform Share %',
        creator_rating: 'Creator Rating',
        opponent_rating: 'Opponent Rating',
        total_ratings: 'Total Ratings',
        finalize_payouts: 'Finalize Payouts',
        payouts_finalized: 'Payouts have been finalized',
        live_numbers: 'Live Numbers',
        updating_realtime: 'Updating in real-time as votes come in',
        revenue_split: 'Revenue Split',
        competition_revenue: 'Competition Revenue',
    },

    // ── T2.4: Viewer ratings & winner ──────────────
    rate_title: 'Rate the competitors',
    rate_thanks: 'Thanks for rating!',
    rate_failed: 'Rating failed',
    rate_winner_banner: 'Winner by public rating',

    // ── B11: anonymous ratings summary + withdrawal ──────────────
    // ── R2-V: provisional live tally + read-only final after close ──
    ratings: {
        summary_title: 'Ratings summary',
        average: 'Average',
        no_ratings: 'No ratings yet',
        withdrawn: 'Your rating has been withdrawn',
        live_provisional: 'Provisional tally — updating live during the broadcast',
        final_readonly: 'Final result — ratings are closed',
        not_eligible: 'Watch 300 seconds of the broadcast to unlock rating',
        update_rating: 'Update rating',
        withdraw_rating: 'Withdraw rating',
        unique_raters: 'Unique raters',
        score_scale_hint: 'Score out of 5',
        draw_label: 'Draw',
        zero_score: 'No score yet',
        live_awaiting: 'Awaiting audience ratings',
        zero_completed: '—',
        early_withdrawal_closed: 'This competition ended by early withdrawal before audience ratings finished — see the system result above.',
    },

    // ── UNIT38 / R4-GUIDE-1 — global SEO defaults (UNIT39 owner revision) ──
    seo: {
        meta_title:
            "Dueli — The World's First Platform for 100% Free & Fair Competitions | Elevate, Learn, Enjoy & Earn",
        default_description:
            "Dueli by Maelsh Pro: The world's first global platform dedicated to 100% free, fair, and ethical competitions across dialogue, science, and talent with absolute transparency. Elevate, learn, enjoy, and earn 80% of ad revenue in a non-profit humanitarian initiative.",
        pages: {
            home: "Dueli by Maelsh Pro: The world's first global platform dedicated to 100% free, fair, and ethical competitions across dialogue, science, and talent with absolute transparency. Elevate, learn, enjoy, and earn 80% of ad revenue in a non-profit humanitarian initiative.",
            explore: 'Explore live and recorded competitions on Dueli by category, subcategory and status — free and fair competitions for dialogue, science and talent by Maelsh Pro.',
            docs: 'Published Dueli documents — the platform notes, policies and owner-approved content its team releases for everyone to read.',
            transparency: 'The open Dueli ledger: money in, money out, the platform share and the cost of running the service.',
            donate: 'Support Dueli with a one-time donation. Dueli is a non-profit humanitarian initiative by Maelsh Pro and publishes its open numbers in the financial transparency ledger.',
        },
    },

    // ── UNIT38 / R4-GUIDE-1 — the /guide center (about + help) ──────────────
    guide: {
        title: 'Dueli Comprehensive Guide — Humanitarian Mission, Technology, AI Acknowledgements & Policies',
        subtitle:
            "Discover Dueli, the world's first platform for free and fair competitions by Maelsh Pro: our humanitarian mission, technical architecture, AI & infrastructure acknowledgements, ethical open-source charter, and official contact channels.",
        hub_intro:
            'Pick the section you need. Each section opens on its own address, so you can bookmark it, share it, or come back to it after a refresh.',
        nav_label: 'Guide sections',
        back_to_center: 'Back to the guide center',
        section_label: 'Section',
        docs_note: 'Published notes and documents live in the Documents section.',
        managed_note:
            'This section shows only what the owner publishes as a managed document. Nothing is listed here before it is published.',
        links: {
            docs: 'Published documents',
            privacy: 'Privacy policy',
            deletion: 'Data deletion instructions',
            reports: 'Report content',
            complaints: 'Track a complaint',
            transparency: 'Financial transparency',
            donate: 'Support the platform',
            messages: 'Admin messages',
        },
        sections: {
            overview: {
                title: 'Overview',
                desc: 'What Dueli is, how a competition runs, and the Maelsh Pro identity behind the platform.',
            },
            message: {
                title: 'Humanitarian mission',
                desc: 'The founder’s human message: four pillars, a future bridge to independent AI agents, and a not-for-profit intent.',
            },
            technology: {
                title: 'Technology',
                desc: 'The real Dueli stack in two layers: a plain-language explanation and the engineering view.',
            },
            ai: {
                title: 'AI acknowledgements',
                desc: 'A technical appreciation of the AI companies, models and platforms that helped build Dueli, and of the friends who shared their personal subscriptions.',
            },
            help: {
                title: 'Help and FAQ',
                desc: 'Role guides, step-by-step topics, answers, and accessibility notes.',
            },
            terms: {
                title: 'Terms and policies',
                desc: 'The rights of competitors and viewers, the versioned policy documents, and the reporting routes that apply.',
            },
            contact: {
                title: 'Contact Maelsh Pro',
                desc: 'The official domains, email addresses, YouTube channel and in-product routes for reaching Dueli and Maelsh Pro.',
            },
            support: {
                title: 'Platform support and continuity',
                desc: 'How ad revenue is shared 80/20, the donor roadmap, non-financial ways to help, and the separation between platform support and competitor earnings.',
            },
        },
        // UNIT39 / R4-GUIDE-2 — the human message (owner-approved revision).
        message_content: {
            slogan: 'Elevate, Learn, Enjoy, and Earn',
            identity_title: 'Who we are',
            identity:
                "Dueli is the world's first platform dedicated to 100% free, fair, and transparent competitions across all fields. Founded and sponsored by Maelsh Pro as a non-profit humanitarian initiative, Dueli aims to replace conflict with dialogue, spread knowledge, and spotlight human talent with absolute transparency, bound only by ethical standards.",
            pillars_title: 'Four pillars',
            pillars: [
                {
                    title: 'Replacing Conflict with Constructive Dialogue (Elevate)',
                    body:
                        'Transforming intellectual and cultural differences from battlegrounds into refined live debates that respect reason and bring people together in mutual respect.',
                },
                {
                    title: 'Simplifying Science & Knowledge for All (Learn)',
                    body:
                        'Bringing complex sciences, theories, and arts closer to the global public through live intellectual competition, making reliable knowledge engaging and accessible across languages.',
                },
                {
                    title: 'Spotlighting Talent & Connecting Competence with Employers (Enjoy)',
                    body:
                        'Providing every talented individual and thinker a fair global stage to showcase their true capabilities through purposeful entertainment. Beyond empowering creators, Dueli serves as a direct bridge for employers, recruiters, and organizations to effortlessly discover verified talent—improving global work quality and placing the right talent in the right place.',
                },
                {
                    title: 'Fair Participatory Economy & Transparent Sustainability (Earn)',
                    body:
                        'Because competing minds are the cornerstone of value, 80% of net ad revenue goes directly to competitors, while 20% is dedicated to platform operations, management, and continuous development.',
                },
            ],
            ai_bridge_title: 'A future bridge for independent AI agents',
            ai_bridge_body:
                "In its future vision, Dueli aspires to build a trusted technical bridge allowing independent AI agents to contribute to human knowledge and civilizational dialogue within a safe, transparent environment—without altering today's authentication or financial systems.",
        },
        // UNIT39 / R4-GUIDE-2 — AI acknowledgements (owner-approved revision).
        ai_content: {
            intro:
                'Rooted in gratitude and absolute transparency, Dueli extends its sincere appreciation to all AI companies and platforms that provide users worldwide with free public tiers and trial credits, as well as the supportive friends who contributed their personal subscriptions. This enabled us to rely on these pioneering models for planning, coding, and reviewing the platform without receiving any special grants or direct funding from these companies. We apologize to any platform or model that contributed to this journey and was inadvertently omitted.',
            company_label: 'Company',
            model_label: 'Models',
            role_label: 'Contribution',
            table_caption: 'AI companies and models that contributed to building Dueli',
            entries: [
                {
                    company: 'OpenAI',
                    model: 'ChatGPT',
                    role: 'Structural planning and project leadership',
                },
                {
                    company: 'Anthropic',
                    model: 'Claude Opus, Claude Sonnet, Claude Haiku',
                    role: 'Coding, architectural review and planning (Claude Opus was accessed through the intermediary platforms listed below)',
                },
                {
                    company: 'Google / Alphabet',
                    model: 'Gemini Argon, Gemini Flash',
                    role: 'Planning leadership, architectural design and comprehensive review (accessed through the intermediary platforms listed below)',
                },
                {
                    company: 'Z.ai',
                    model: 'GLM, GLM Flash',
                    role: 'Coding, planning and technical review',
                },
                {
                    company: 'Moonshot AI',
                    model: 'Kimi K',
                    role: 'Analysis and planning',
                },
            ],
            additional_title: 'Other leading models that proved highly capable',
            additional_intro:
                'These models were also used through free tiers or the personal subscriptions of supportive friends:',
            additional: [
                { company: 'DeepSeek', model: 'DeepSeek Flash' },
                { company: 'MiniMax', model: 'MiniMax Flash' },
                { company: 'Meta / Muse', model: 'Muse Spark & Muse Wonder' },
                { company: 'Other providers', model: 'Other supporting intelligent models' },
            ],
            platforms_title: 'Platforms that provided access',
            platforms_intro:
                'The following platforms and intelligent development environments provided access to these models:',
            platforms: [
                'Genspark AI',
                'OpenRouter',
                'Cursor',
                'Antigravity',
                'Google AI Studio',
                'Cline Code',
                'Kilo Code',
                'OpenCode',
            ],
            infrastructure_title: 'Hosting and infrastructure',
            infrastructure_body:
                'Special thanks to GitHub, the home of the source code and its continuous quality gates, and to Cloudflare, the global edge network, Workers, Pages and the D1 database that carry the platform.',
            appeal_title: 'An appeal for the common good',
            appeal:
                'We encourage all technology and AI companies worldwide to expand free access for humanitarian and public-good initiatives. Without such generosity, Dueli would not have seen the light of day—together, technology and human values can make the world a better place.',
            disclaimer:
                'These companies, platforms, and models are mentioned solely out of technical appreciation for the public tools that helped build Dueli, without implying any commercial partnership, official sponsorship, mutual obligation, or ownership rights.',
            updates_title: 'How this list is updated',
            updates_body:
                'This list is part of the platform source text. Any addition or correction is recorded in a managed document through the existing admin dashboard (H9), which appears under Documents; no separate content system is introduced.',
            docs_link: 'Published documents',
        },
        // UNIT39 / R4-GUIDE-2 — two-layer technology page (owner-approved revision).
        technology_content: {
            intro:
                'This page explains the technologies Dueli actually uses, in two layers: a plain-language view for everyone and an engineering view for specialists. No technology is listed unless the current code uses it.',
            simple_title: 'In plain terms',
            simple_paragraphs: [
                'Dueli runs in the browser on Cloudflare’s global network. Two competitors meet in a live video call between their devices (WebRTC peer-to-peer); when a network blocks the direct path, a TURN relay helps the connection through.',
                'During a competition the browser combines both camera feeds into one picture and records it as short 5-second chunks. The chunks are uploaded as they are produced, can be played back in order, and are assembled into a full replay after the match ends.',
                'Recorded competitions can be downloaded as an MP4 file. When a recording needs conversion, the conversion runs inside the visitor’s browser with FFmpeg.wasm, so the file is not sent to an external conversion service.',
                'Platform data lives in D1, pages and APIs are built with Hono and TypeScript on Cloudflare, and the interface uses Tailwind CSS. Live updates arrive through SSE and polling.',
            ],
            engineering_title: 'For engineers',
            engineering_paragraphs: [
                'Runtime: Cloudflare Pages/Workers with Hono 4 on TypeScript. D1 (SQLite) is the database. Vite builds the Worker bundle, Tailwind CSS 4 styles the interface, and esbuild bundles the browser entry points.',
                'Live media: a browser RTCPeerConnection carries WebRTC between host and opponent. ICE servers are issued per session from GET /api/signaling/ice-servers; Cloudflare Calls TURN (TURN_TOKEN_ID and TURN_API_TOKEN) or self-hosted coturn (TURN_URL and TURN_SECRET) can relay, with a STUN-only fallback when neither is configured.',
                'Recording: a canvas compositor draws both feeds, and MediaRecorder emits a chunk every 5 seconds. The host registers each chunk at POST /api/chunks/register (a short-lived one-time key bound to the competition, sequence and byte ceiling), uploads it, verifies it with GET /verify and reports POST /receipt. Playback reads GET /api/chunks/playlist/:id, and the existing finalize step produces the VOD.',
                'Downloads: @ffmpeg/core 0.12.10 runs in a self-hosted browser worker for MP4 conversion, with a structural remux path for recordings that are already MP4-compatible. Progress and memory limits are handled in the client service.',
                'Realtime: SSE through GET /api/sse carries live updates, and signaling uses HTTP polling; the production path does not use WebSocket.',
                'Quality: Vitest covers unit and UI contracts, and Playwright covers end-to-end browser flows. Markdown documents are rendered with marked, and PostCSS with Autoprefixer completes the CSS pipeline.',
            ],
            stack_title: 'The stack in this repository',
            stack: [
                { name: 'Cloudflare Pages / Workers', role: 'Hosting and server runtime' },
                { name: 'Cloudflare D1 (SQLite)', role: 'Application database' },
                { name: 'Hono 4', role: 'HTTP routes, pages and API' },
                { name: 'TypeScript 5', role: 'Typed source for server and client' },
                { name: 'Tailwind CSS 4 + PostCSS + Autoprefixer', role: 'Interface styling and CSS pipeline' },
                { name: 'Vite 7 + esbuild', role: 'Worker bundle and browser entry points' },
                { name: 'WebRTC (RTCPeerConnection) + TURN/STUN', role: 'Live audio and video between participants' },
                { name: 'Canvas compositor + MediaRecorder', role: '5-second recorded chunks in the browser' },
                { name: 'FFmpeg.wasm (@ffmpeg/core 0.12.10)', role: 'In-browser MP4 conversion and download' },
                { name: 'marked 17', role: 'Markdown rendering for managed documents' },
                { name: 'Vitest 5', role: 'Unit and UI test suite' },
                { name: 'Playwright', role: 'End-to-end browser tests' },
                { name: 'Wrangler 4', role: 'Cloudflare development and deployment tooling' },
                { name: 'SSE + HTTP polling', role: 'Live updates outside the media path' },
            ],
            note: 'The list is limited to what package.json and the repository actually contain. It is a technical description, not a partnership or endorsement claim.',
        },
        // UNIT39 / R4-GUIDE-2 — support, donors and the 80/20 model.
        support_content: {
            intro:
                'How the platform is supported, how advertising revenue is shared, and how Dueli is sustained as a non-profit humanitarian initiative.',
            split_title: 'Advertising revenue: an 80% / 20% split',
            split_80_title: '80% to competitors',
            split_80_body:
                'The competitor pool is divided between the two sides of a competition in proportion to each side’s average audience rating, and equally when the result is a draw or no ratings exist — following the existing payout cycle.',
            split_20_title: '20% for platform operation, sustainability and development',
            split_20_body:
                'This share covers running costs, maintenance and development. The percentage is configurable in the platform settings, and the open figures are published in the financial transparency ledger.',
            split_note:
                'This describes the existing model; it is not a promise of earnings or dates. Future development plans are open to donor sponsorship under the published terms.',
            donors_title: 'Donors and supporters — a staged vision',
            donors: [
                {
                    title: 'Stage 1: publish what is spent',
                    body: 'Keep the open ledger current so anyone can see what comes in and what goes out.',
                },
                {
                    title: 'Stage 2: make one-time support clearer',
                    body: 'Improve the existing one-time support route and explain exactly what it funds; no new payment provider is introduced for this.',
                },
                {
                    title: 'Stage 3: recognise supporters',
                    body: 'If a recognition surface is added later, it will be announced through the published documents. This is a direction, not a dated commitment.',
                },
            ],
            donors_note:
                'These stages are a direction, not guaranteed dates. Any future support channel must use the existing payment path and receive owner approval before it is announced.',
            future_title: 'Future development, open to donor sponsorship',
            future_body:
                'Approved future development plans are published as they are prepared and are open to donor sponsorship through the existing one-time support route. No new payment provider or payment path is introduced, and no earnings or dates are promised.',
            nonfinancial_title: 'Ways to contribute without money',
            nonfinancial: [
                'Share the idea with people who would use it, and talk about the project honestly.',
                'Contribute to the parts the project publishes openly, under their published licence.',
                'Report a problem or suggest an improvement through the existing report and support routes.',
            ],
            separation_title: 'Platform support is not competitor earnings',
            separation_body:
                'Support for the platform goes through /donate and its open figures appear in /transparency. Competitor earnings come from the existing payout cycle after a competition, and audience donations to competitors are a separate flow inside the product. The two are never mixed, and this page opens no new payment route.',
        },
        // UNIT39 / R4-GUIDE-2 — terms and rights (owner-approved revision).
        terms_content: {
            intro:
                'This page sets out the rights and duties that apply on Dueli, and links them to the published policy documents. The versioned document in Documents is the reference; this page is a plain-language guide.',
            clauses_title: 'Terms and rights',
            clauses: [
                {
                    title: '1. Media rights, promotional use and platform revenue',
                    body:
                        'By creating or participating in a competition on Dueli, competitors grant the platform administration and Maelsh Pro a perpetual, worldwide, non-exclusive, and irrevocable license to use, reproduce, and republish all audio, video, and text media generated on the platform—in whole or in part—across social media (including YouTube) and any other media channels for promotional, archival, and outreach purposes. No competitor may demand the prevention or removal of such publication, and any revenue generated from external publication may be utilized by the platform administration as it deems appropriate to support, operate, and develop Dueli.',
                    items: [
                        'Competitors: both competitors may download and republish their own competition material and benefit from it through their own channels, under the published terms.',
                        'Viewers: viewers may watch and download competition material for personal, non-commercial use only. Public or commercial republication requires prior permission from the platform.',
                    ],
                },
                {
                    title: '2. Open-source ethical licence and continuity charter',
                    body:
                        'Dueli publishes its source code transparently in service of humanity under three legally and ethically binding conditions:',
                    items: [
                        'Good-use mandate: no entity or individual may use the platform’s source code except for benevolent purposes that serve humanity and knowledge. It may not be used in any activity that violates ethical values or harms communities.',
                        'No commercial use without authorization: no individual or entity may use the source code for profit or commercial gain without direct, prior written authorization and coordination with the original platform administration (Maelsh Pro).',
                        'Transparency and continuity protocol (anti-coercion safeguard): should the platform or its administration ever face external coercive pressures resulting in a clear, demonstrable breach of transparency or a deviation from its humanitarian mission, any strictly non-profit party is authorized to deploy and operate the platform’s source code at their own expense without restriction to preserve its mission—subject to one mandatory condition: allocating the 20% platform share of ad revenue to the original founding administration (Maelsh Pro) to support its efforts in overcoming such restrictions and restoring the platform.',
                    ],
                },
                {
                    title: '3. Privacy and data protection',
                    body:
                        'Dueli handles account data, secure HttpOnly sessions, and the broadcasts, comments and ratings a user chooses to publish. Personal data is never sold to third parties. Users may edit their profile, change their password, and delete a pending competition before an opponent joins.',
                },
                {
                    title: '4. Competitor ad control',
                    body:
                        'Competitors keep ethical control over the commercial advertisements shown during their competitions through their account settings, including manual or automatic approval, allow-lists and block-lists. Dueli’s own official house ads are exempt.',
                },
                {
                    title: '5. Financial and voting integrity',
                    body:
                        'An audience rating counts only after a verified minimum watch time of 300 seconds. Automated manipulation of views, ad clicks or ratings is prohibited, and early withdrawal is settled under the existing atomic settlement rules.',
                },
                {
                    title: '6. Code of conduct and content disclaimer',
                    body:
                        'Competitors must follow the ethics of dialogue. Hate speech, defamation and legal violations are prohibited. Opinions expressed in competitions belong to the competitors who express them and do not necessarily represent Dueli or Maelsh Pro.',
                },
                {
                    title: '7. Versioned policy governance (H9)',
                    body:
                        'Terms and policies are managed as versioned documents with a version and an update date in /docs through the H9 system. Any acceptance step is documented there before it is activated.',
                },
            ],
            policies_title: 'Policies that already apply',
            note:
                'This page is not a substitute for the published legal documents. Final legal review remains part of the dedicated release process.',
        },
        contact_content: {
            intro:
                'The official channels and domains of Dueli and Maelsh Pro are the ones published on this page and in the official documents.',
            domains_title: 'Official domains',
            domains: [
                {
                    label: 'dueli.win',
                    href: 'https://dueli.win',
                    desc: 'Official splash and redirect domain: it opens the welcome page and then redirects to the working platform.',
                },
                {
                    label: 'dueli.maelshpro.com',
                    href: 'https://dueli.maelshpro.com',
                    desc: 'The current working platform address, a subdomain of the founding company.',
                },
            ],
            channels_title: 'Official in-product channels',
            channels: [
                {
                    label: 'Admin messages',
                    desc: 'A private support thread inside the platform; signing in is required.',
                },
                {
                    label: 'Report content',
                    desc: 'Report a competition, profile, comment or advertisement for review.',
                },
                {
                    label: 'Track a complaint',
                    desc: 'Follow a complaint you already submitted and its status.',
                },
            ],
            external_title: 'Official email and external channels',
            external: [
                {
                    label: 'dueli@maelshpro.com',
                    href: 'mailto:dueli@maelshpro.com',
                    desc: 'Official email of the Dueli platform.',
                },
                {
                    label: 'info@maelshpro.com',
                    href: 'mailto:info@maelshpro.com',
                    desc: 'Official email of the founding and sponsoring company, Maelsh Pro.',
                },
                {
                    label: 'YouTube — @Maelsh-Dueli',
                    href: 'https://www.youtube.com/@Maelsh-Dueli',
                    desc: 'Official YouTube channel of Dueli.',
                },
            ],
            security_title: 'Official-channel notice',
            security_notice:
                'These channels and domains are the only official channels of Dueli and Maelsh Pro. Any account or channel not listed on this page or in the official documents does not represent the platform.',
            response_note:
                'No specific response time is promised. Reports and support are handled through the existing systems.',
        },
        // OBS Virtual Camera guide (hosts / presenters).
        obs: {
            title: 'Guide: OBS Virtual Camera for presenters',
            intro:
                'OBS can expose a virtual camera that Dueli can pick up like any other webcam. This guide shows the shortest path from OBS to a live camera inside the competition room.',
            step_1_t: 'Prepare your scene in OBS',
            step_1_d:
                'Add the sources you want to broadcast (a screen, a window, a camera) and arrange them in one scene before you continue.',
            step_2_t: 'Start the virtual camera in OBS',
            step_2_d:
                'In the OBS Controls panel press “Start Virtual Camera”. OBS then registers a camera named “OBS Virtual Camera” with your operating system.',
            step_3_t: 'Let the browser use that camera',
            step_3_d:
                'When Dueli asks for the camera, the browser shows the camera it will use. Pick “OBS Virtual Camera” there — the room itself has no device picker.',
            step_4_t: 'Turn the camera on in the room',
            step_4_d:
                'In the competition room press the camera button to switch the camera on, and check the local preview shows your OBS scene.',
            step_5_t: 'Publish to go live',
            step_5_d:
                'The preview stays on your device. Press the publish button to send your camera to the other side and the audience.',
            alt_t: 'Sharing the OBS window instead',
            alt_d:
                'You can also share the OBS window with the screen-share button. That path needs no virtual camera and no browser camera selection.',
            note_t: 'About the camera buttons',
            note_d:
                'The camera button switches the camera on and off, and the switch-camera button swaps the front and back camera on a phone — it does not choose between devices. Device choice is made in the browser or in the operating system.',
        },
    },

    // ── UNIT38 / R4-GUIDE-1 — the /welcome splash portal ──────────────
    splash: {
        title: 'Dueli',
        word_1: 'Elevate',
        word_2: 'Learn',
        word_3: 'Enjoy',
        word_4: 'Earn',
        by_maelsh_pro: 'By Maelsh Pro',
        skip: 'Skip and enter Dueli now',
        redirecting: 'Taking you to Dueli…',
        redirect_failed: 'Redirect failed. Open the link above.',
    },

    // ── T1.3: Streaming ──────────────
    recording_processing: 'Preparing the recording...',
    // ── R4-CHUNK-PLAY-1: audience live/VOD states ──────────────
    recording_not_ready: 'Recording not available',
    // ── UNIT30.6 D-02: honest state for a completed competition with no recording ──────────────
    recording_none: 'No recording for this competition',
    streaming_unavailable: 'Streaming service unavailable',
    connecting_to_stream: 'Connecting to stream...',
    loading_video: 'Loading video...',
    stream_ended: 'Stream ended',
    chunk: 'Chunk',

    // ── T3.3: Nested comments ──────────────
    reply: 'Reply',
    replying_to: 'Replying to:',

    // ── R2-L2: VOD timed comments ──────────────
    vod_comment_at: 'Jump to moment',

    // ── T3.2: Profile posts ──────────────
    post_placeholder: 'Share a thought with your audience...',
    post_publish: 'Publish',
    confirm_delete_post: 'Delete this post?',

    // ── T3.2: Account deletion (GDPR) ──────────────
    danger_zone: 'Danger Zone',
    delete_account: 'Delete Account',
    delete_account_warning: 'Deleting your account is permanent and cannot be undone.',
    confirm_delete_account: 'Are you sure you want to permanently delete your account? Your identity will be anonymized and your data removed.',
    account_deleted: 'Your account has been deleted. Sorry to see you go.',

    // ── T2.2: Real-time SSE notifications ──────────────
    sse: {
        new_invite: 'invites you to compete!',
        invite_accepted: 'accepted your invite ✅',
        invite_declined: 'declined your invite',
    },

    // ── Task 10: Dynamic Matchmaking & Invite Panel ──────────────
    matchmaking: {
        panel_title: 'Invite Opponent',
        search_users: 'Search users...',
        online_now: 'Online Now',
        recently_active: 'Recently Active',
        offline: 'Offline',
        send_invite: 'Send Invite',
        invite_sent: 'Invite Sent!',
        sending: 'Sending...',
        no_users_available: 'No users available right now',
        already_has_opponent: 'Competition already has an opponent',
        competition_not_open: 'Competition is not open for invites',
        compatibility: 'Compatibility',
        compatibility_unavailable: 'Unavailable',
        wins_label: 'Wins',
        competitions_label: 'Competitions',
        rating_label: 'Rating',
        close_panel: 'Close Panel',
        refresh: 'Refresh',
        auto_updating: 'Auto-updating every 10s',
        blocked_user: 'User blocked',
        busy_status: 'Currently busy',
        invite_opponent_btn: 'Invite Opponent',
        stats_winrate: 'Win Rate',
        stats_losses: 'Losses',
        stats_top_categories: 'Top Fields',
        view_profile: 'View Profile',
        loading_users: 'Loading available users...',
    },

    // ── Task 7: Advanced Recommendation Engine ──────────────────
    recommendations: {
        carousel_title: 'Recommended For You',
        no_recommendations: 'No recommendations yet. Start watching competitions!',
        competitor_stats: 'Competitor Stats',
        losses: 'Losses',
        win_rate: 'Win Rate',
        top_categories: 'Top Categories',
        degraded_notice: 'Showing broader results for you',
        for_you: 'For You',
        trending: 'Trending',
        empty: 'No competitions match your preferences right now. Check back later!',
    },

    // ── Task 8: Competition Lifecycle & Strict Timers ───────────
    timers: {
        time_to_join: 'Time left to join:',
        time_to_start: 'Time to start:',
        broadcast_ends_in: 'Broadcast ends in',
        expired: 'Time expired',
    },
};

